import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
// Los colores de los estados, cargados una vez al entrar (entrega 278).
import { ColoresEstadoProvider } from './context/ColoresEstadoContext';
import { rutaInicial } from './utils/menu';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';

const Dashboard    = React.lazy(() => import('./pages/Dashboard'));
const Entidades    = React.lazy(() => import('./pages/Entidades'));
const Clientes     = React.lazy(() => import('./pages/Clientes'));
const Vehiculos    = React.lazy(() => import('./pages/Vehiculos'));
const Trabajadores = React.lazy(() => import('./pages/Trabajadores'));
const Ausencias    = React.lazy(() => import('./pages/Ausencias'));
const OtrosTrabajos = React.lazy(() => import('./pages/OtrosTrabajos'));
const ResumenPersonal = React.lazy(() => import('./pages/ResumenPersonal'));
const DiarioTrabajador = React.lazy(() => import('./pages/DiarioTrabajador'));
const Presupuestos = React.lazy(() => import('./pages/Presupuestos'));
const Agenda       = React.lazy(() => import('./pages/Agenda'));
const Trafico      = React.lazy(() => import('./pages/Trafico'));
const LineaTiempo  = React.lazy(() => import('./pages/LineaTiempo'));
const Historico    = React.lazy(() => import('./pages/Historico'));
const Extras       = React.lazy(() => import('./pages/Extras'));
const Calendarios  = React.lazy(() => import('./pages/Calendarios'));
const Recurrentes  = React.lazy(() => import('./pages/Recurrentes'));
const Avisos       = React.lazy(() => import('./pages/Avisos'));
const Usuarios     = React.lazy(() => import('./pages/Usuarios'));
const Roles        = React.lazy(() => import('./pages/Roles'));
const TiposAviso   = React.lazy(() => import('./pages/TiposAviso'));
const ColoresEstado = React.lazy(() => import('./pages/ColoresEstado'));
const MisServicios = React.lazy(() => import('./pages/MisServicios'));
const HojasRuta    = React.lazy(() => import('./pages/HojasRuta'));
const AutorizacionesRuta = React.lazy(() => import('./pages/AutorizacionesRuta'));
const MiDocumentacion = React.lazy(() => import('./pages/MiDocumentacion'));
const DocumentacionPersonal = React.lazy(() => import('./pages/DocumentacionPersonal'));
const Perfil       = React.lazy(() => import('./pages/Perfil'));

// ─── Ruta protegida por permiso ───────────────────────────────────
// permiso  → código único ('trafico')
// alguno   → array de códigos; basta con tener uno (agenda, histórico)
// Sin ninguno de los dos: solo exige estar autenticado (Perfil).
function PrivateRoute({ children, permiso, alguno }) {
  const { user, loading, puede, puedeAlguno } = useAuth();

  if (loading) return (
    <div className="spinner-center" style={{ minHeight:'100vh' }}>
      <div className="spinner" style={{ width:40, height:40 }} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  const autorizado = permiso ? puede(permiso, 'ver')
                   : alguno  ? puedeAlguno(alguno, 'ver')
                   : true;

  // Sin permiso → a la primera pantalla que sí pueda ver (nunca un bucle
  // de redirecciones hacia /dashboard, que quizá tampoco tenga)
  if (!autorizado) return <Navigate to={rutaInicial(puede)} replace />;

  return children;
}

// ─── Redirección de la raíz ───────────────────────────────────────
function Inicio() {
  const { puede } = useAuth();
  return <Navigate to={rutaInicial(puede)} replace />;
}

function AppRoutes() {
  const { user, puede } = useAuth();
  return (
    <React.Suspense fallback={<div className="spinner-center"><div className="spinner" /></div>}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={rutaInicial(puede)} replace /> : <Login />} />
        <Route path="/" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route index element={<Inicio />} />
          <Route path="dashboard"     element={<PrivateRoute permiso="dashboard"><Dashboard /></PrivateRoute>} />
          <Route path="entidades"     element={<PrivateRoute permiso="entidades"><Entidades /></PrivateRoute>} />
          <Route path="clientes"      element={<PrivateRoute permiso="clientes"><Clientes /></PrivateRoute>} />
          <Route path="vehiculos"     element={<PrivateRoute permiso="vehiculos"><Vehiculos /></PrivateRoute>} />
          <Route path="trabajadores"  element={<PrivateRoute permiso="trabajadores"><Trabajadores /></PrivateRoute>} />
          <Route path="ausencias"     element={<PrivateRoute permiso="ausencias"><Ausencias /></PrivateRoute>} />
          <Route path="otros-trabajos" element={<PrivateRoute permiso="otros_trabajos"><OtrosTrabajos /></PrivateRoute>} />
          <Route path="resumen-personal" element={<PrivateRoute permiso="resumen_personal"><ResumenPersonal /></PrivateRoute>} />
          {/* Diario de una persona. No lleva entrada de menú: se entra desde el
              Resumen del personal pulsando su nombre. */}
          {/* El diario lleva los extras: se entra con permiso de Extras (entrega 285). */}
          <Route path="diario/:id" element={<PrivateRoute permiso="extras"><DiarioTrabajador /></PrivateRoute>} />
          <Route path="presupuestos"  element={<PrivateRoute permiso="presupuestos"><Presupuestos /></PrivateRoute>} />
          <Route path="agenda"        element={<PrivateRoute alguno={['agenda_recurrente','agenda_discrecional']}><Agenda /></PrivateRoute>} />
          <Route path="trafico"       element={<PrivateRoute permiso="trafico"><Trafico /></PrivateRoute>} />
          {/* Vista de línea de tiempo del cuadrante. Mismo permiso que Tráfico:
              es la misma información, mirada de otra manera. */}
          <Route path="trafico/linea-tiempo" element={<PrivateRoute permiso="trafico"><LineaTiempo /></PrivateRoute>} />
          <Route path="historico"     element={<PrivateRoute alguno={['historico_recurrente','historico_discrecional']}><Historico /></PrivateRoute>} />
          <Route path="extras"        element={<PrivateRoute permiso="extras"><Extras /></PrivateRoute>} />
          <Route path="calendarios"   element={<PrivateRoute permiso="calendarios"><Calendarios /></PrivateRoute>} />
          <Route path="recurrentes"   element={<PrivateRoute permiso="recurrentes"><Recurrentes /></PrivateRoute>} />
          {/* Cualquiera de los cuatro permisos de avisos abre la pantalla:
              lo que se ve dentro ya lo recorta el servidor. */}
          <Route path="avisos"        element={<PrivateRoute alguno={['avisos','avisos_trabajador','avisos_vehiculo','avisos_empresa','avisos_autorizaciones']}><Avisos /></PrivateRoute>} />
          <Route path="usuarios"      element={<PrivateRoute permiso="usuarios"><Usuarios /></PrivateRoute>} />
          <Route path="roles"         element={<PrivateRoute permiso="roles"><Roles /></PrivateRoute>} />
          <Route path="tipos-aviso"   element={<PrivateRoute permiso="tipos_aviso"><TiposAviso /></PrivateRoute>} />
          {/* Colores de los estados (entrega 278). Permiso propio. */}
          <Route path="colores-estado" element={<PrivateRoute permiso="estados_color"><ColoresEstado /></PrivateRoute>} />
          <Route path="mis-servicios" element={<PrivateRoute permiso="mis_servicios"><MisServicios /></PrivateRoute>} />
          <Route path="hojas-ruta"    element={<PrivateRoute permiso="hojas_ruta"><HojasRuta /></PrivateRoute>} />
          <Route path="autorizaciones-ruta" element={<PrivateRoute permiso="autorizaciones_ruta"><AutorizacionesRuta /></PrivateRoute>} />
          <Route path="mi-documentacion" element={<PrivateRoute permiso="mi_documentacion"><MiDocumentacion /></PrivateRoute>} />
          <Route path="documentacion-personal" element={<PrivateRoute permiso="documentacion_personal"><DocumentacionPersonal /></PrivateRoute>} />
          <Route path="perfil"        element={<PrivateRoute><Perfil /></PrivateRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ColoresEstadoProvider>
          <AppRoutes />
        </ColoresEstadoProvider>
        <Toaster position="top-right" toastOptions={{
          duration: 3500, style: { fontSize:14, maxWidth:380 },
          success: { iconTheme: { primary:'#0d5d3a', secondary:'#fff' } },
        }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
